import { createListenerMiddleware } from '@reduxjs/toolkit'
import { LOCATION_CHANGE } from 'lib/routing/appRouting'
import {
  SET_BYPASS_REGION_EXCLUSION_OFFER_IDS,
  SET_BYPASS_REGION_SETTING_ID,
  SET_FREE_PREVIEW_AUTO_ENROL,
  SET_FREE_PREVIEW_AUTO_ENROL_FOR_EXISTING_USERS,
} from 'actions/actionConstants'
import { getRegionChannelSettings, REGION_CHANNEL_SETTING_TYPE } from 'api/promo'
import { getCookies, setCookie } from 'lib/web/cookieUtils'
import config from 'constants/config'

// Client-side fallback for SPA navigations where SSR didn't seed the region/channel settings.
// Mirrors fetchRegionChannelSettings on the server: one svc-promo request resolves every setting,
// and each setting keeps its own cookie state.
//
// Offer region bypass (MLOS): the cookie marks that this user's region + UTM matched an enabled
// bypass rule, so the bypass stays on for the rest of the visit without re-resolving.
const MATCHED_COOKIE_KEY = 'bypass_region_setting_id'
const EXCLUSION_IDS_COOKIE_KEY = 'bypass_region_exclusion_ids'
const BYPASS_COOKIE_TTL_SECONDS = 7 * 24 * 60 * 60 // 7 days

// Free Preview auto-enrolment: eligibility persists until the rule's end_datetime, capped at
// 7 days from the matching visit, so a return visit still enrols.
const FP_MAX_COOKIE_TTL_SECONDS = 7 * 24 * 60 * 60

// The two Free Preview audiences resolve and persist identically and differ only in who they
// target — the same table as the server's fetchRegionChannelSettings, plus how each one reads
// "already settled" off the store.
const FREE_PREVIEW_SETTINGS = [
  {
    settingType: REGION_CHANNEL_SETTING_TYPE.freePreviewAutoEnrol,
    action: SET_FREE_PREVIEW_AUTO_ENROL,
    cookieKey: 'fp_auto_enrol',
    // Read lazily so tests can flip the brand config between runs.
    isEnabled: () => config.ENABLE_AUTO_ENROLLMENT_FOR_FREE_PREVIEW,
    isResolved: (state: App.State) => !!state.marketing.freePreviewAutoEnrol,
  },
  {
    settingType: REGION_CHANNEL_SETTING_TYPE.freePreviewAutoEnrolForExistingUsers,
    action: SET_FREE_PREVIEW_AUTO_ENROL_FOR_EXISTING_USERS,
    cookieKey: 'fp_auto_enrol_existing',
    isEnabled: () => config.ENABLE_AUTO_ENROLLMENT_FOR_FREE_PREVIEW_EXISTING_USERS,
    isResolved: (state: App.State) => !!state.marketing.freePreviewAutoEnrolForExistingUsers,
  },
] as const

type FreePreviewSetting = typeof FREE_PREVIEW_SETTINGS[number]

const fpCookieMaxAgeSeconds = (endDatetime?: string | null): number => {
  if (!endDatetime) return FP_MAX_COOKIE_TTL_SECONDS
  const remaining = Math.floor((new Date(endDatetime).getTime() - Date.now()) / 1000)
  return Math.max(0, Math.min(remaining, FP_MAX_COOKIE_TTL_SECONDS))
}

const regionChannelSettingsMiddleware = createListenerMiddleware()

// A matched setting id turns the bypass on for this visit.
function applyMatchedBypassSetting(
  listenerApi: { dispatch: (action: unknown) => unknown },
  matchedSettingId: string,
) {
  listenerApi.dispatch({ type: SET_BYPASS_REGION_SETTING_ID, settingId: matchedSettingId })
}

regionChannelSettingsMiddleware.startListening({
  type: LOCATION_CHANGE,
  effect: async(_action, listenerApi) => {
    listenerApi.unsubscribe()

    const state = listenerApi.getState() as App.State
    const cookies = getCookies()

    // 1. Settle each setting from state or its cookie where possible, so we only hit svc-promo
    //    when at least one of them is unresolved.
    const unresolvedFreePreviews: Array<FreePreviewSetting> = []
    for (const setting of FREE_PREVIEW_SETTINGS) {
      if (!setting.isEnabled() || setting.isResolved(state)) continue
      const savedSettingId = cookies[setting.cookieKey]
      if (savedSettingId) {
        listenerApi.dispatch({ type: setting.action, enabled: true, settingId: savedSettingId })
      } else {
        unresolvedFreePreviews.push(setting)
      }
    }

    let needBypass = !state.marketing.bypassRegionSettingId
    if (needBypass) {
      const savedBypassSettingId = cookies[MATCHED_COOKIE_KEY]
      if (savedBypassSettingId) {
        const savedIds = cookies[EXCLUSION_IDS_COOKIE_KEY]
        if (savedIds) {
          listenerApi.dispatch({ type: SET_BYPASS_REGION_EXCLUSION_OFFER_IDS, offerIds: savedIds.split(',') })
        }
        applyMatchedBypassSetting(listenerApi, savedBypassSettingId)
        needBypass = false
      }
    }

    if (unresolvedFreePreviews.length === 0 && !needBypass) return

    const { source, medium, campaign, term, content } = state.utm
    const region = state.geo.currentRegionCode

    try {
      const settings = await getRegionChannelSettings({
        region: region || undefined,
        source: source || undefined,
        medium: medium || undefined,
        campaign: campaign || undefined,
        term: term || undefined,
        content: content || undefined,
      })

      for (const setting of unresolvedFreePreviews) {
        const resolution = settings[setting.settingType]
        listenerApi.dispatch({
          type: setting.action,
          enabled: resolution?.enabled ?? false,
          settingId: resolution?.setting_id ?? null,
        })
        if (resolution?.enabled) {
          setCookie(
            setting.cookieKey,
            resolution.setting_id ?? '1',
            fpCookieMaxAgeSeconds(resolution.end_datetime),
            '/',
            'Lax',
            document.domain,
          )
        }
      }

      if (needBypass) {
        const bypass = settings[REGION_CHANNEL_SETTING_TYPE.offerRegionFilterBypass]

        // Offer exclusions are global and experiment-independent — always applied when present.
        const excludedOfferIds = bypass?.excluded_offer_ids ?? []
        if (excludedOfferIds.length > 0) {
          setCookie(EXCLUSION_IDS_COOKIE_KEY, excludedOfferIds.join(','), BYPASS_COOKIE_TTL_SECONDS, '/', 'Lax', document.domain)
          listenerApi.dispatch({ type: SET_BYPASS_REGION_EXCLUSION_OFFER_IDS, offerIds: excludedOfferIds })
        }

        // A disabled winning rule means no bypass, same as no rule at all.
        if (bypass?.enabled && bypass.setting_id) {
          setCookie(MATCHED_COOKIE_KEY, bypass.setting_id, BYPASS_COOKIE_TTL_SECONDS, '/', 'Lax', document.domain)
          applyMatchedBypassSetting(listenerApi, bypass.setting_id)
        }
      }
    } catch {
      // non-critical — both settings stay unset
    }
  },
})

export default regionChannelSettingsMiddleware
